// Minimal stroked lucide-style icon set, ported from the prototype's ui.jsx.
// Each icon is a small component taking standard SVG props so callers control size via
// className (e.g. `<Icon.home className="w-4 h-4" />`) and colour via `currentColor`.
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const base: IconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

export const Icon = {
  home: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M3 11.5L12 4l9 7.5" />
      <path d="M5 10.5V20h14v-9.5" />
      <path d="M10 20v-5h4v5" />
    </svg>
  ),
  clip: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  // Solid play triangle — for the thumbnail overlay on saved clips.
  play: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none" />
    </svg>
  ),
  check: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 12.5l3 3 5-6" />
    </svg>
  ),
  flask: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 13l2 2 6-5" />
    </svg>
  ),
  bookmark: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M6 3h12v18l-6-4-6 4z" />
    </svg>
  ),
  doc: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  trophy: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M8 4h8v5a4 4 0 0 1-8 0V4z" />
      <path d="M5 4h3v3H6a2 2 0 0 1-2-2V4h1zM16 4h3v1a2 2 0 0 1-2 2h-2V4z" />
      <path d="M10 13h4v3h2v3H8v-3h2v-3z" />
    </svg>
  ),
  chart: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 3 3 5-6" />
    </svg>
  ),
  upload: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M12 4v12" />
      <path d="M7 9l5-5 5 5" />
      <path d="M4 17v3h16v-3" />
    </svg>
  ),
  whistle: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M3 12a6 6 0 1 0 12 0H3z" />
      <path d="M15 9l6-3v6l-6-3z" />
    </svg>
  ),
  search: (p: IconProps) => (
    <svg {...base} {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  ),
  bell: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M6 16V11a6 6 0 1 1 12 0v5l2 2H4z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  ),
  spark: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
    </svg>
  ),
  arrow: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M5 12h14" />
      <path d="M13 5l7 7-7 7" />
    </svg>
  ),
  arrowDown: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M7 10l5 5 5-5" />
    </svg>
  ),
  lock: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" />
    </svg>
  ),
  // Calendar — timing affordance on the auto-timer readout.
  calendar: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  ),
  menu: (p: IconProps) => (
    <svg {...base} {...p} strokeWidth={1.7}>
      <path d="M4 7h16M4 12h16M4 17h10" />
    </svg>
  ),
  more: (p: IconProps) => (
    <svg {...base} {...p} strokeWidth={1.8}>
      <circle cx="5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="19" cy="12" r="1.2" fill="currentColor" />
    </svg>
  ),
  plus: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  chevronLeft: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M15 6l-6 6 6 6" />
    </svg>
  ),
  chevronRight: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  close: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  send: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M3 11L21 3l-7 18-3-8z" />
    </svg>
  ),
  mail: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  ),
  copy: (p: IconProps) => (
    <svg {...base} {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h8" />
    </svg>
  ),
  users: (p: IconProps) => (
    <svg {...base} {...p}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3.2 3.2 0 0 1 0 6M17 19a5.5 5.5 0 0 0-2.5-4.6" />
    </svg>
  ),
  logout: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2" />
      <path d="M10 12h11M18 9l3 3-3 3" />
    </svg>
  ),
  building: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
      <path d="M16 9h2a2 2 0 0 1 2 2v10" />
      <path d="M3 21h18M8 7h2M8 11h2M8 15h2" />
    </svg>
  ),
  trash: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M10 11v7M14 11v7" />
    </svg>
  ),
  // Pencil — inline edit affordance.
  // Chain link — a webinar's meeting URL (join / copy link).
  link: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
    </svg>
  ),
  pencil: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
      <path d="M13.5 6.5l3 3" />
    </svg>
  ),
  // Eye-with-slash — fits an Unpublish action (taking the clip out of public view).
  eyeOff: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M3 3l18 18" />
      <path d="M10.6 6.1A10.7 10.7 0 0 1 12 6c5 0 9 6 9 6a17 17 0 0 1-2.6 3.3" />
      <path d="M6.1 6.1A17 17 0 0 0 3 12s4 6 9 6a10.7 10.7 0 0 0 4.6-1.1" />
      <path d="M9.5 9.5a3 3 0 0 0 4.2 4.2" />
    </svg>
  ),
  filter: (p: IconProps) => (
    <svg {...base} {...p}>
      <path d="M3 5h18l-7 8v6l-4-2v-4z" />
    </svg>
  ),
} as const;

export type IconName = keyof typeof Icon;
